import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, ShieldAlert, Wallet } from "lucide-react";
import { frApi, errMsg, inr, fieldCls, labelCls, cardCls, btnPrimary, FEAS, APPROVAL, toPayload } from "./frUtils";
import QuoteActions from "./QuoteActions";

const outside = (v, r) => v !== "" && v != null && (Number(v) < r.min_fee || Number(v) > r.max_fee);

export default function NegotiationCard({ form, set, result, record, setRecord }) {
    const [saving, setSaving] = useState(false);
    const rng = result.negotiation;
    const unit = rng.basis === "monthly" ? "per month" : rng.basis === "package" ? "for the package" : "per session";
    const b = result.budget;
    const needsApproval = outside(form.proposed_fee, rng) || outside(form.final_fee, rng);
    const save = async () => {
        setSaving(true);
        try {
            const rec = await frApi.post("/records", toPayload(form));
            setRecord(rec);
            toast.success(rec.approval_status === "pending" ? "Saved. Sent to management for approval." : "Saved to history");
        } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
    };
    return (
        <div className={`${cardCls} space-y-4`} data-testid="fr-negotiation">
            <h3 className="font-heading font-bold text-[#0F172A] flex items-center gap-2"><Wallet className="w-4 h-4 text-[#1E3A8A]" /> Parent's expected budget</h3>
            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                <input type="number" min={1} inputMode="decimal" value={form.budget_amount} onChange={(e) => set({ budget_amount: e.target.value })}
                    placeholder="Amount in ₹ (optional)" className={fieldCls} data-testid="fr-budget-amount" />
                <select value={form.budget_basis} onChange={(e) => set({ budget_basis: e.target.value })} className={`${fieldCls} !w-auto`} data-testid="fr-budget-basis">
                    <option value="per_session">per session</option>
                    <option value="weekly">per week</option>
                    <option value="monthly">per month</option>
                </select>
            </div>
            {b && (
                <div className={`rounded-xl border p-3 text-sm space-y-1 ${FEAS[b.status].cls}`} data-testid={`fr-feasibility-${b.status}`}>
                    <div className="font-bold" data-testid="fr-feasibility-label">{FEAS[b.status].tag}. {b.label}</div>
                    <div>Budget works out to <b>{inr(b.per_session)}</b> per session · <b>{inr(b.monthly)}</b> per month.</div>
                    <div>{b.advice}</div>
                    <div>Suggested quote: <b data-testid="fr-suggested-quote">{inr(b.suggested_quote)}</b> per month</div>
                </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <label className={labelCls} htmlFor="fr-proposed">Your proposed fee ({unit})</label>
                    <input id="fr-proposed" type="number" min={1} value={form.proposed_fee} onChange={(e) => set({ proposed_fee: e.target.value })} className={fieldCls} data-testid="fr-proposed-fee" placeholder={`${rng.min_fee} – ${rng.max_fee}`} />
                </div>
                <div>
                    <label className={labelCls} htmlFor="fr-final">Final negotiated fee ({unit})</label>
                    <input id="fr-final" type="number" min={1} value={form.final_fee} onChange={(e) => set({ final_fee: e.target.value })} className={fieldCls} data-testid="fr-final-fee" placeholder="When agreed" />
                </div>
            </div>
            {needsApproval && (
                <div className="flex gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" data-testid="fr-approval-warning">
                    <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                    <span><b>D. Management approval required.</b> This fee is outside the approved {inr(rng.min_fee)} – {inr(rng.max_fee)} {unit} range. Saving sends it for approval, and no quotation can be shared until it's approved.</span>
                </div>
            )}
            <textarea value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} placeholder="Notes (optional)" className={`${fieldCls} !h-auto py-2`} data-testid="fr-notes" />
            {record ? (
                <div className="space-y-3">
                    <div className="text-sm flex items-center gap-2" data-testid="fr-record-saved">
                        Saved · <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${APPROVAL[record.approval_status].cls}`}>{APPROVAL[record.approval_status].label}</span>
                    </div>
                    <QuoteActions record={record} onRecord={setRecord} />
                </div>
            ) : (
                <button type="button" onClick={save} disabled={saving} className={`${btnPrimary} w-full`} data-testid="fr-save-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {needsApproval ? "Save & request approval" : "Save to history"}
                </button>
            )}
        </div>
    );
}
